/**
 * Outil « Calcul malin » (Boîte à outils, 6e → 2nde).
 *
 * Stratégies de calcul mental sur deux nombres de 1 à 999 : de tête quand
 * c'est immédiat, passer la dizaine (38 + 7), compléter (201 − 198), par
 * rangs, arrondir et compenser (39 + 52 = 40 + 50 + 2 − 1), écart constant
 * (82 − 39 = 83 − 40), découper un produit, arrondir un facteur (99 × 37),
 * ×5 / ×25 / ×50, double distributivité dès la 4e (49 × 19 = (50 − 1)(20 − 1))
 * et identités remarquables en 3e (51², 49 × 51).
 * La méthode conseillée vient d'abord ; les étapes se déroulent une à une
 * pour essayer de tête avant, et la figure avance avec elles : sauts sur une
 * droite (au-dessus on ajoute, en dessous on enlève), deux écarts égaux,
 * grille de produits partiels signés, ou modèle d'aire (49 × 19 dans
 * 50 × 20 : deux bandes retirées qui se recouvrent, le coin rajouté).
 * Calculatrice interdite sur l'exercice (`sendCost` > 0) : la dernière étape
 * reste à faire de tête. Défi : 5 calculs. Calculs : `strategies.ts`.
 */

import { useEffect, useRef, useState } from 'react';
import { Shuffle } from 'lucide-react';
import { schoolLevelRank } from '@jeraisonne/db/cards';
import { MathText, cn } from '@jeraisonne/ui';
import { SendToSheetButton } from '../../send-to-sheet-button';
import type { ToolProps } from '../../tool-types';
import {
  IconButton,
  Note,
  NumberField,
  PRIMARY_BUTTON,
  SECONDARY_BUTTON,
  Segmented,
} from '../../ui/controls';
import { arrowHeadPoints, FIGURE_BG_CLASS, HaloText } from '../../ui/svg-parts';
import {
  fmt,
  MAX,
  MIN,
  OP_SIGN,
  plansFor,
  randomCalc,
  result,
  type Op,
  type Plan,
  type StrategyId,
} from './strategies';

const SERIES = 5;
const ADD = 'var(--color-primary-500)';
const ADD_TEXT = 'var(--color-primary-600)';
const SUB = 'var(--color-error-500)';
const SUB_TEXT = 'var(--color-error-600)';
const BOTH = 'var(--color-success-600)';
const PILL_STROKE = 'var(--color-ink-400)';
const NNBSP = String.fromCharCode(0x202f);

export function SmartCalcTool({
  onSend,
  sendCost,
  level,
  rng = Math.random,
}: ToolProps & { level?: string | null; rng?: () => number }) {
  // 6e = 1 … 3e = 4 ; au-delà (2nde) ou sans niveau, tout ce qui existe en 3e
  const rank = Math.min(4, schoolLevelRank(level) ?? 4);
  const [op, setOp] = useState<Op>('add');
  const [mode, setMode] = useState<'explore' | 'challenge'>('explore');
  const [ab, setAb] = useState({ a: 39, b: 52 });
  const [nonce, setNonce] = useState(0);

  const examples: Record<Op, { a: number; b: number }> = {
    add: { a: 39, b: 52 },
    sub: { a: 82, b: 39 },
    mul: rank >= 3 ? { a: 49, b: 19 } : { a: 99, b: 37 },
  };

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap gap-2">
        <Segmented
          ariaLabel="Opération"
          value={op}
          options={[
            { value: 'add', label: 'Addition' },
            { value: 'sub', label: 'Soustraction' },
            { value: 'mul', label: 'Multiplication' },
          ]}
          onChange={(v) => {
            if (v === op) return;
            setOp(v);
            setAb(examples[v]);
          }}
        />
        <Segmented
          ariaLabel="Mode"
          value={mode}
          options={[
            { value: 'explore', label: 'Explorer' },
            { value: 'challenge', label: 'Défi' },
          ]}
          onChange={setMode}
        />
      </div>
      {mode === 'explore' ? (
        <Explorer
          key={`${op}-${nonce}`}
          op={op}
          a={ab.a}
          b={ab.b}
          rank={rank}
          onChange={setAb}
          onRandom={() => {
            setAb(randomCalc(op, rank, rng));
            setNonce((n) => n + 1);
          }}
          onSend={onSend}
          sendCost={sendCost}
        />
      ) : (
        <Challenge key={op} op={op} rank={rank} rng={rng} />
      )}
    </div>
  );
}

function Explorer({
  op,
  a,
  b,
  rank,
  onChange,
  onRandom,
  onSend,
  sendCost,
}: {
  op: Op;
  a: number;
  b: number;
  rank: number;
  onChange: (v: { a: number; b: number }) => void;
  onRandom: () => void;
  onSend?: ToolProps['onSend'];
  sendCost: number;
}) {
  const [pick, setPick] = useState<StrategyId | null>(null);
  const [shown, setShown] = useState(0);
  const plans = plansFor(op, a, b, rank);
  const plan = plans.find((p) => p.id === pick) ?? plans[0];
  // calculatrice interdite sur l'exercice : la dernière étape reste à faire de tête
  const keepLast = sendCost > 0;
  const total = plan ? plan.steps.length - (keepLast ? 1 : 0) : 0;
  const done = !!plan && !keepLast && shown >= plan.steps.length;
  const set = (v: { a: number; b: number }) => {
    onChange(v);
    setShown(0);
    setPick(null);
  };

  return (
    <>
      <div className="grid grid-cols-2 gap-2">
        <NumberField
          label="1er nombre"
          value={a}
          min={MIN}
          max={MAX}
          onChange={(v) => set({ a: v, b })}
        />
        <NumberField
          label="2e nombre"
          value={b}
          min={MIN}
          max={MAX}
          onChange={(v) => set({ a, b: v })}
        />
      </div>
      <div className="flex items-center gap-2">
        <p
          className="font-display text-ink-900 min-w-0 flex-1 text-2xl font-bold tabular-nums"
          data-testid="sc-calc"
        >
          {fmt(a)}
          {NNBSP}
          {OP_SIGN[op]}
          {NNBSP}
          {fmt(b)}
          {NNBSP}={NNBSP}
          {done && plan ? fmt(plan.result) : '?'}
        </p>
        <IconButton label="Nouveau calcul" onClick={onRandom}>
          <Shuffle className="h-5 w-5" aria-hidden />
        </IconButton>
      </div>

      {!plan ? (
        <Note>
          {op === 'sub' && b > a
            ? 'Pour soustraire ici, le 1er nombre doit être au moins aussi grand que le 2e.'
            : 'Pas de méthode pour ce calcul.'}
        </Note>
      ) : (
        <>
          {plans.length > 1 ? (
            <>
              <div className="flex flex-wrap gap-2" role="group" aria-label="Méthode">
                {plans.map((p, i) => (
                  <button
                    key={p.id}
                    type="button"
                    aria-pressed={p.id === plan.id}
                    onClick={() => {
                      setPick(p.id);
                      setShown(0);
                    }}
                    className={cn(
                      'min-h-11 rounded-xl border px-3 text-sm font-semibold transition-colors active:scale-[0.98]',
                      p.id === plan.id
                        ? 'border-primary-500 bg-primary-50 text-primary-700'
                        : 'border-ink-200 text-ink-600 hover:bg-ink-50',
                    )}
                  >
                    {i === 0 && <span aria-hidden>★ </span>}
                    {p.name}
                    {i === 0 && <span className="sr-only"> (conseillée)</span>}
                  </button>
                ))}
              </div>
              <p className="text-ink-500 text-xs">★ méthode conseillée pour ces nombres</p>
            </>
          ) : (
            <p className="text-ink-700 text-sm font-semibold">{plan.name}</p>
          )}

          <Figure plan={plan} shown={shown} done={done} />

          <section
            className="border-ink-100 space-y-2 rounded-2xl border px-3 py-3"
            aria-label="Étapes"
            data-testid="sc-steps"
          >
            {shown === 0 && plan.id !== 'direct' && (
              <p className="text-ink-600 text-sm">
                Essaie de tête avec la méthode « {plan.name} », puis déroule les étapes.
              </p>
            )}
            <ol className="space-y-2" aria-live="polite">
              {plan.steps.slice(0, Math.min(shown, total)).map((s, i) => (
                <li key={i} className="space-y-0.5">
                  <MathText className="text-ink-900 block overflow-x-auto text-lg">{`$${s.tex}$`}</MathText>
                  {s.say && <p className="text-ink-600 text-sm">{s.say}</p>}
                </li>
              ))}
            </ol>
            {keepLast && shown >= total && (
              <Note live={false}>
                Calculatrice non autorisée sur cet exercice : la dernière étape est pour toi.
              </Note>
            )}
            <div className="flex flex-wrap items-center gap-2">
              {shown < total ? (
                <>
                  <button
                    type="button"
                    className={PRIMARY_BUTTON}
                    onClick={() => setShown((v) => Math.min(total, v + 1))}
                  >
                    {shown === 0 ? 'Première étape' : 'Étape suivante'}
                  </button>
                  <button
                    type="button"
                    className={SECONDARY_BUTTON}
                    onClick={() => setShown(total)}
                  >
                    Tout voir
                  </button>
                </>
              ) : (
                <>
                  <button type="button" className={SECONDARY_BUTTON} onClick={() => setShown(0)}>
                    Recommencer
                  </button>
                  {onSend && done && (
                    <SendToSheetButton
                      label={String(plan.result)}
                      cost={sendCost}
                      onClick={() => onSend({ atoms: [String(plan.result)] })}
                      className="ml-auto"
                    />
                  )}
                </>
              )}
            </div>
          </section>
        </>
      )}
    </>
  );
}

/** Défi : 5 calculs de tête ; après chaque réponse, la méthode conseillée. */
function Challenge({ op, rank, rng }: { op: Op; rank: number; rng: () => number }) {
  const [calcs, setCalcs] = useState(() =>
    Array.from({ length: SERIES }, () => randomCalc(op, rank, rng)),
  );
  const [i, setI] = useState(0);
  const [text, setText] = useState('');
  const [answers, setAnswers] = useState<boolean[]>([]);
  const [invalid, setInvalid] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);
  const nextRef = useRef<HTMLButtonElement>(null);
  const calc = calcs[i];
  const answered = answers.length > i;
  const good = answers.filter(Boolean).length;

  // le focus suit le défi : bouton « suivant » après une réponse, champ pour un nouveau calcul
  useEffect(() => {
    if (answered) nextRef.current?.focus();
    else inputRef.current?.focus({ preventScroll: true });
  }, [answered, i]);

  const check = () => {
    if (!calc || answered) return;
    const clean = text.replace(/[\s\u202F]/g, '').replace('−', '-');
    if (!/^-?\d+$/.test(clean)) {
      setInvalid(true);
      return;
    }
    setInvalid(false);
    setAnswers((old) => [...old, Number(clean) === result(op, calc.a, calc.b)]);
  };
  const restart = () => {
    setCalcs(Array.from({ length: SERIES }, () => randomCalc(op, rank, rng)));
    setI(0);
    setText('');
    setAnswers([]);
  };

  if (!calc) return null;
  const plan = plansFor(op, calc.a, calc.b, rank)[0];
  const right = answers[i];
  const value = result(op, calc.a, calc.b);
  return (
    <section className="space-y-3" aria-label="Défi" data-testid="sc-challenge">
      <p className="text-ink-600 text-sm">
        Calcul {i + 1}/{SERIES} · {good} juste{good > 1 ? 's' : ''}
      </p>
      <p className="font-display text-ink-900 text-3xl font-bold tabular-nums">
        {fmt(calc.a)}
        {NNBSP}
        {OP_SIGN[op]}
        {NNBSP}
        {fmt(calc.b)}
        {NNBSP}={NNBSP}
        {answered ? fmt(value) : '?'}
      </p>
      <form
        className="flex flex-wrap items-center gap-2"
        onSubmit={(e) => {
          e.preventDefault();
          check();
        }}
      >
        <input
          ref={inputRef}
          type="text"
          inputMode="numeric"
          aria-label="Ta réponse"
          aria-invalid={invalid}
          value={text}
          disabled={answered}
          onChange={(e) => {
            setText(e.target.value);
            setInvalid(false);
          }}
          className="border-ink-200 text-ink-900 font-display min-h-11 w-32 rounded-xl border bg-transparent px-3 text-lg font-bold tabular-nums"
        />
        <button type="submit" className={PRIMARY_BUTTON} disabled={answered}>
          Valider
        </button>
      </form>
      {/* zone toujours présente : les lecteurs d'écran annoncent ce qui y apparaît */}
      <div className="space-y-2" aria-live="polite" data-testid="sc-feedback">
        {invalid && !answered && (
          <p className="text-error-600 text-sm font-semibold">Écris un nombre entier.</p>
        )}
        {answered && (
          <>
            <Note tone={right ? 'success' : 'error'} live={false}>
              {right
                ? `Juste${NNBSP}! Voici une méthode rapide${NNBSP}:`
                : `Pas tout à fait${NNBSP}: c’était ${fmt(value)}. Une méthode rapide${NNBSP}:`}
            </Note>
            {plan && (
              <div className="space-y-1">
                <p className="text-ink-900 text-sm font-semibold">{plan.name}</p>
                {plan.steps.map((s, j) => (
                  <MathText
                    key={j}
                    className="text-ink-800 block overflow-x-auto"
                  >{`$${s.tex}$`}</MathText>
                ))}
              </div>
            )}
          </>
        )}
      </div>
      {answered &&
        (i + 1 < SERIES ? (
          <button
            ref={nextRef}
            type="button"
            className={PRIMARY_BUTTON}
            onClick={() => {
              setI(i + 1);
              setText('');
            }}
          >
            Calcul suivant
          </button>
        ) : (
          <div className="flex flex-wrap items-center gap-2">
            <p className="text-ink-900 text-sm font-bold" data-testid="sc-score">
              Score : {good}/{SERIES}
            </p>
            <button ref={nextRef} type="button" className={SECONDARY_BUTTON} onClick={restart}>
              Nouvelle série
            </button>
          </div>
        ))}
    </section>
  );
}

function Figure({ plan, shown, done }: { plan: Plan; shown: number; done: boolean }) {
  if (plan.line) {
    // le saut k apparaît avec son étape : la figure ne donne pas la réponse d'avance
    const visible = done
      ? plan.line.jumps.length
      : Math.max(0, Math.min(plan.line.jumps.length, shown - plan.line.firstJumpStep));
    return (
      <LineFigure
        start={plan.line.start}
        jumps={plan.line.jumps}
        visible={visible}
        gap={!!plan.line.gap}
        done={done}
      />
    );
  }
  if (plan.shift) return <ShiftFigure shift={plan.shift} done={done} />;
  if (plan.area) return <AreaFigure rows={plan.area.rows} cols={plan.area.cols} done={done} />;
  if (plan.grid) return <GridFigure rows={plan.grid.rows} cols={plan.grid.cols} done={done} />;
  return null;
}

const W = 320;
const LINE_H = 150;
const LINE_Y = 75;
const MIN_JUMP = 48;
const PAD = 32;

function Pill({ x, y, label, strong }: { x: number; y: number; label: string; strong?: boolean }) {
  const w = 12 + 7.5 * label.length;
  return (
    <g>
      <rect
        x={x - w / 2}
        y={y - 11}
        width={w}
        height={22}
        rx={11}
        fill="var(--fig-bg, #fff)"
        stroke={strong ? 'var(--color-accent-700)' : PILL_STROKE}
        strokeWidth={strong ? 2 : 1.25}
      />
      <text
        x={x}
        y={y + 4.5}
        textAnchor="middle"
        fontSize={13}
        fontWeight={700}
        fill="var(--color-ink-900)"
      >
        {label}
      </text>
    </g>
  );
}

/**
 * Droite « vide » : les nombres dans l'ordre, écarts compressés (√) avec un
 * minimum, au-dessus on ajoute, en dessous on enlève. Seuls les `visible`
 * premiers sauts sont tracés. `gap` (compléter) : on cherche la longueur des
 * sauts, l'arrivée est connue.
 */
function LineFigure({
  start,
  jumps,
  visible,
  gap,
  done,
}: {
  start: number;
  jumps: number[];
  visible: number;
  gap: boolean;
  done: boolean;
}) {
  const values = [start];
  jumps.forEach((j, i) => values.push(values[i]! + j));
  const sorted = [...new Set(values)].sort((x, y) => x - y);
  const gaps = sorted.slice(1).map((v, i) => Math.sqrt(v - sorted[i]!));
  const sumGaps = gaps.reduce((s, x) => s + x, 0) || 1;
  const spare = Math.max(0, W - 2 * PAD - MIN_JUMP * gaps.length);
  const xOf = new Map<number, number>();
  let x = sorted.length === 1 ? W / 2 : PAD;
  sorted.forEach((v, i) => {
    if (i > 0) x += MIN_JUMP + (spare * gaps[i - 1]!) / sumGaps;
    xOf.set(v, x);
  });
  const xs = values.map((v) => xOf.get(v)!);
  const last = values.length - 1;
  const total = jumps.reduce((s, j) => s + j, 0);
  return (
    <div className="mx-auto w-full max-w-[26rem]">
      <svg
        viewBox={`0 0 ${W} ${LINE_H}`}
        className={cn('border-ink-100 block w-full rounded-2xl border', FIGURE_BG_CLASS)}
        role="img"
        aria-label={`Sur la droite : départ ${fmt(start)}${
          visible
            ? `, puis ${jumps
                .slice(0, visible)
                .map((j) => (j > 0 ? `plus ${fmt(j)}` : `moins ${fmt(-j)}`))
                .join(', ')}`
            : ''
        }${gap ? `, arrivée ${fmt(values[last]!)}` : ''}.`}
        data-testid="sc-line"
      >
        <line
          x1={Math.min(...xs) - 16}
          x2={Math.max(...xs) + 16}
          y1={LINE_Y}
          y2={LINE_Y}
          stroke={PILL_STROKE}
          strokeWidth={2}
        />
        {jumps.slice(0, visible).map((j, i) => {
          const x1 = xs[i]!;
          const x2 = xs[i + 1]!;
          const up = j > 0;
          const h = Math.min(52, 18 + Math.abs(x2 - x1) * 0.28);
          const cy = up ? LINE_Y - 2 * h : LINE_Y + 2 * h;
          const mid = (x1 + x2) / 2;
          const color = up ? ADD : SUB;
          return (
            <g key={i}>
              <path
                d={`M ${x1} ${LINE_Y} Q ${mid} ${cy} ${x2} ${LINE_Y}`}
                fill="none"
                stroke={color}
                strokeWidth={2.5}
              />
              <polygon
                points={arrowHeadPoints(mid, cy, x2, LINE_Y + (up ? -10 : 10), 10)}
                fill={color}
              />
              <HaloText
                x={mid}
                y={up ? LINE_Y - h - 6 : LINE_Y + h + 16}
                textAnchor="middle"
                fontSize={13}
                fontWeight={700}
                fill={up ? ADD_TEXT : SUB_TEXT}
              >
                {up ? '+' : '−'}
                {fmt(Math.abs(j))}
              </HaloText>
            </g>
          );
        })}
        {values.map((v, i) =>
          // l'arrivée connue (compléter) reste affichée ; sinon chaque point arrive avec son saut
          i <= visible || (gap && i === last) ? (
            <Pill key={i} x={xs[i]!} y={LINE_Y} label={fmt(v)} strong={i === last} />
          ) : null,
        )}
        {gap && (
          <HaloText
            x={W / 2}
            y={LINE_H - 10}
            textAnchor="middle"
            fontSize={13}
            fontWeight={700}
            fill="var(--color-accent-700)"
          >
            écart : {done ? fmt(total) : '?'}
          </HaloText>
        )}
      </svg>
      <p className="text-ink-500 mt-1 text-center text-xs">
        Au-dessus on ajoute, en dessous on enlève (sauts pas à l’échelle).
      </p>
    </div>
  );
}

/** Écart constant : b → a, puis les deux décalés d'autant, le même écart. */
function ShiftFigure({
  shift,
  done,
}: {
  shift: { from: [number, number]; to: [number, number] };
  done: boolean;
}) {
  const s = shift.to[0] - shift.from[0];
  const xb = 70;
  const xa = 230;
  const dx = s > 0 ? 22 : -22;
  const rows: Array<{ y: number; pair: [number, number]; dx: number }> = [
    { y: 50, pair: shift.from, dx: 0 },
    { y: 128, pair: shift.to, dx },
  ];
  const sign = s > 0 ? '+' : '−';
  const color = s > 0 ? ADD : SUB;
  return (
    <div className="mx-auto w-full max-w-[26rem]">
      <svg
        viewBox={`0 0 ${W} 160`}
        className={cn('border-ink-100 block w-full rounded-2xl border', FIGURE_BG_CLASS)}
        role="img"
        aria-label={`Écart constant : de ${fmt(shift.from[0])} à ${fmt(shift.from[1])}, autant que de ${fmt(shift.to[0])} à ${fmt(shift.to[1])}.`}
        data-testid="sc-shift"
      >
        {rows.map(({ y, pair, dx: off }, r) => (
          <g key={r}>
            <line x1={14} x2={W - 14} y1={y} y2={y} stroke={PILL_STROKE} strokeWidth={2} />
            <path
              d={`M ${xb + off} ${y - 14} Q ${(xb + xa) / 2 + off} ${y - 44} ${xa + off} ${y - 14}`}
              fill="none"
              stroke="var(--color-accent-700)"
              strokeWidth={2}
            />
            <HaloText
              x={(xb + xa) / 2 + off}
              y={y - 32}
              textAnchor="middle"
              fontSize={12}
              fontWeight={700}
              fill="var(--color-accent-700)"
            >
              écart {done ? fmt(pair[1] - pair[0]) : '?'}
            </HaloText>
            <Pill x={xb + off} y={y} label={fmt(pair[0])} />
            <Pill x={xa + off} y={y} label={fmt(pair[1])} />
          </g>
        ))}
        {/* le décalage, sur chacun des deux nombres */}
        {[xb, xa].map((x0) => (
          <g key={x0}>
            <line
              x1={x0}
              y1={64}
              x2={x0 + dx}
              y2={106}
              stroke={color}
              strokeWidth={1.75}
              strokeDasharray="4 3"
            />
            <polygon points={arrowHeadPoints(x0, 64, x0 + dx, 110, 8)} fill={color} />
            <HaloText
              x={x0 + dx / 2 + (s > 0 ? 10 : -10)}
              y={90}
              textAnchor={s > 0 ? 'start' : 'end'}
              fontSize={12}
              fontWeight={700}
              fill={s > 0 ? ADD_TEXT : SUB_TEXT}
            >
              {sign}
              {fmt(Math.abs(s))}
            </HaloText>
          </g>
        ))}
      </svg>
    </div>
  );
}

const HEAD = 46;
const MIN_COL = 58;
const MIN_ROW = 38;

/** largeurs (ou hauteurs) proportionnelles aux morceaux, avec un minimum lisible */
function share(xs: number[], avail: number, min: number): number[] {
  const total = xs.reduce((s, x) => s + Math.abs(x), 0) || 1;
  const spare = Math.max(0, avail - min * xs.length);
  return xs.map((x) => min + (spare * Math.abs(x)) / total);
}

const headText = (v: number) => (v < 0 ? `−${fmt(-v)}` : fmt(v));
const factor = (v: number) => (v < 0 ? `(−${fmt(-v)})` : fmt(v));

/** Grille de produits partiels : chaque case = morceau de l'un × morceau de l'autre, signe compris. */
function GridFigure({ rows, cols, done }: { rows: number[]; cols: number[]; done: boolean }) {
  const ws = share(cols, W - HEAD - 8, MIN_COL);
  const hs = share(rows, Math.min(190, MIN_ROW * rows.length + 110), MIN_ROW);
  const xs = ws.map((_, i) => HEAD + ws.slice(0, i).reduce((s, x) => s + x, 0));
  const ys = hs.map((_, i) => 30 + hs.slice(0, i).reduce((s, x) => s + x, 0));
  const H = 30 + hs.reduce((s, x) => s + x, 0) + 8;
  const anyNeg = rows.some((r) => r < 0) || cols.some((c) => c < 0);
  const bothNeg = rows.some((r) => r < 0) && cols.some((c) => c < 0);
  return (
    <div className="mx-auto w-full max-w-[26rem]">
      <svg
        viewBox={`0 0 ${W} ${H}`}
        className={cn('border-ink-100 block w-full rounded-2xl border', FIGURE_BG_CLASS)}
        role="img"
        aria-label={`Grille : ${rows.map(headText).join(' et ')} fois ${cols.map(headText).join(' et ')}${done ? ` ; cases ${rows.flatMap((r) => cols.map((c) => headText(r * c))).join(', ')}` : ''}.`}
        data-testid="sc-grid"
      >
        {cols.map((c, j) => (
          <text
            key={`c${j}`}
            x={xs[j]! + ws[j]! / 2}
            y={20}
            textAnchor="middle"
            fontSize={13}
            fontWeight={700}
            fill={c < 0 ? SUB_TEXT : 'var(--color-ink-800)'}
          >
            {headText(c)}
          </text>
        ))}
        {rows.map((r, i) => (
          <text
            key={`r${i}`}
            x={HEAD - 8}
            y={ys[i]! + hs[i]! / 2 + 4.5}
            textAnchor="end"
            fontSize={13}
            fontWeight={700}
            fill={r < 0 ? SUB_TEXT : 'var(--color-ink-800)'}
          >
            {headText(r)}
          </text>
        ))}
        {rows.map((r, i) =>
          cols.map((c, j) => {
            const v = r * c;
            const both = r < 0 && c < 0;
            const neg = v < 0;
            const color = both ? BOTH : neg ? SUB : ADD;
            const label = done ? (neg ? `−${fmt(-v)}` : both ? `+${fmt(v)}` : fmt(v)) : null;
            const cx = xs[j]! + ws[j]! / 2;
            const cy = ys[i]! + hs[i]! / 2;
            // avant le calcul, les deux facteurs ; sur deux lignes si la case est étroite
            const expr = `${factor(r)}×${factor(c)}`;
            const twoLines = !label && expr.length * 7 > ws[j]! - 6;
            return (
              <g key={`${i}-${j}`}>
                <rect
                  x={xs[j]! + 1.5}
                  y={ys[i]! + 1.5}
                  width={ws[j]! - 3}
                  height={hs[i]! - 3}
                  rx={6}
                  fill={color}
                  fillOpacity={0.16}
                  stroke={color}
                  strokeWidth={1.5}
                  strokeDasharray={neg ? '5 3' : undefined}
                />
                <text
                  x={cx}
                  y={twoLines ? cy - 3 : cy + 4.5}
                  textAnchor="middle"
                  fontSize={label && label.length * 8 < ws[j]! - 6 ? 14 : 12}
                  fontWeight={700}
                  fill={both ? BOTH : neg ? SUB_TEXT : 'var(--color-ink-900)'}
                >
                  {label ??
                    (twoLines ? (
                      <>
                        <tspan x={cx}>{`${factor(r)}×`}</tspan>
                        <tspan x={cx} dy={14}>
                          {factor(c)}
                        </tspan>
                      </>
                    ) : (
                      expr
                    ))}
                </text>
              </g>
            );
          }),
        )}
      </svg>
      {anyNeg && (
        <p className="text-ink-500 mt-1 text-center text-xs">
          Pointillés rouges : on enlève.
          {bothNeg && ' Case verte : (−) × (−) = +, on l’ajoute.'}
        </p>
      )}
    </div>
  );
}

/**
 * Modèle d'aire de (m − d)(n − e) : le grand rectangle m × n, dont on retire
 * une bande de d sur toute la hauteur… et une de e sur toute la largeur. Les
 * deux bandes se recouvrent dans le coin : il a été retiré deux fois, on le
 * rajoute une fois. Pas à l'échelle (les bandes restent visibles).
 */
function AreaFigure({
  rows,
  cols,
  done,
}: {
  rows: [number, number];
  cols: [number, number];
  done: boolean;
}) {
  const [m, d] = [rows[0], -rows[1]];
  const [n, e] = [cols[0], -cols[1]];
  const X0 = 52;
  const Y0 = 34;
  const RW = W - X0 - 16;
  const RH = 170;
  const band = 30;
  const H = Y0 + RH + 34;
  const keepW = RW - band;
  const keepH = RH - band;
  const val = (v: number) => (done ? fmt(v) : null);
  return (
    <div className="mx-auto w-full max-w-[26rem]">
      <svg
        viewBox={`0 0 ${W} ${H}`}
        className={cn('border-ink-100 block w-full rounded-2xl border', FIGURE_BG_CLASS)}
        role="img"
        aria-label={`Rectangle de ${fmt(m)} sur ${fmt(n)}. On retire une bande de ${fmt(d)} sur ${fmt(n)} et une bande de ${fmt(m)} sur ${fmt(e)} ; elles se recouvrent dans un coin de ${fmt(d)} sur ${fmt(e)}, retiré deux fois, qu’on rajoute.`}
        data-testid="sc-area"
      >
        {/* cotes */}
        <text
          x={X0 + RW / 2}
          y={18}
          textAnchor="middle"
          fontSize={13}
          fontWeight={700}
          fill="var(--color-ink-800)"
        >
          {fmt(n)}
        </text>
        <text
          x={X0 + keepW + band / 2}
          y={Y0 - 4}
          textAnchor="middle"
          fontSize={12}
          fontWeight={700}
          fill={SUB_TEXT}
        >
          −{fmt(e)}
        </text>
        <text
          x={X0 - 8}
          y={Y0 + RH / 2}
          textAnchor="end"
          fontSize={13}
          fontWeight={700}
          fill="var(--color-ink-800)"
        >
          {fmt(m)}
        </text>
        <text
          x={X0 - 8}
          y={Y0 + keepH + band / 2 + 4}
          textAnchor="end"
          fontSize={12}
          fontWeight={700}
          fill={SUB_TEXT}
        >
          −{fmt(d)}
        </text>
        {/* le grand rectangle m × n */}
        <rect
          x={X0}
          y={Y0}
          width={RW}
          height={RH}
          rx={4}
          fill={ADD}
          fillOpacity={0.14}
          stroke={ADD}
          strokeWidth={2}
        />
        <HaloText
          x={X0 + keepW / 2}
          y={Y0 + keepH / 2 + 5}
          textAnchor="middle"
          fontSize={14}
          fontWeight={700}
          fill="var(--color-ink-900)"
        >
          {done ? `${fmt(m)} × ${fmt(n)} = ${fmt(m * n)}` : `${fmt(m)} × ${fmt(n)}`}
        </HaloText>
        {/* bande du bas : d × n (toute la largeur) */}
        <rect
          x={X0}
          y={Y0 + keepH}
          width={RW}
          height={band}
          fill={SUB}
          fillOpacity={0.18}
          stroke={SUB}
          strokeWidth={1.5}
          strokeDasharray="5 3"
        />
        <HaloText
          x={X0 + keepW / 2}
          y={Y0 + keepH + band / 2 + 4.5}
          textAnchor="middle"
          fontSize={12}
          fontWeight={700}
          fill={SUB_TEXT}
        >
          {val(d * n) ? `−${val(d * n)}` : `−${fmt(d)}×${fmt(n)}`}
        </HaloText>
        {/* bande de droite : m × e (toute la hauteur) */}
        <rect
          x={X0 + keepW}
          y={Y0}
          width={band}
          height={RH}
          fill={SUB}
          fillOpacity={0.18}
          stroke={SUB}
          strokeWidth={1.5}
          strokeDasharray="5 3"
        />
        <HaloText
          x={X0 + keepW + band / 2}
          y={Y0 + keepH / 2}
          textAnchor="middle"
          fontSize={12}
          fontWeight={700}
          fill={SUB_TEXT}
          transform={`rotate(-90 ${X0 + keepW + band / 2} ${Y0 + keepH / 2})`}
        >
          {val(m * e) ? `−${val(m * e)}` : `−${fmt(m)}×${fmt(e)}`}
        </HaloText>
        {/* le coin, dans les deux bandes : retiré deux fois → rajouté */}
        <rect
          x={X0 + keepW}
          y={Y0 + keepH}
          width={band}
          height={band}
          fill={BOTH}
          fillOpacity={0.3}
          stroke={BOTH}
          strokeWidth={2}
        />
        <text
          x={X0 + keepW + band / 2}
          y={Y0 + keepH + band / 2 + 4.5}
          textAnchor="middle"
          fontSize={12}
          fontWeight={700}
          fill={BOTH}
        >
          +{fmt(d * e)}
        </text>
      </svg>
      <p className="text-ink-500 mt-1 text-center text-xs">
        Les deux bandes rouges se recouvrent dans le coin vert : il est retiré deux fois, on le
        rajoute une fois (pas à l’échelle).
      </p>
    </div>
  );
}
